import { json } from '@codemirror/lang-json';
import {
  LRLanguage,
  LanguageSupport,
  delimitedIndent,
  foldEffect,
  foldService,
  foldable,
  indentNodeProp,
  syntaxTree,
  syntaxTreeAvailable,
} from '@codemirror/language';
import { type Extension, type StateEffect } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { SyntaxNode } from '@lezer/common';
import { parser } from '@lezer/yaml';
import { useTheme } from '@mui/joy';

import * as YAML from 'yaml';

import { useColorScheme } from '@/hooks/useColorScheme';

const yamlParser = parser.configure({
  props: [
    indentNodeProp.add({
      Stream: (cx) => {
        for (
          let before = cx.node.resolve(cx.pos, -1) as SyntaxNode | null;
          before && before.to >= cx.pos;
          before = before.parent
        ) {
          if (before.name == 'BlockLiteralContent' && before.from < before.to)
            return cx.baseIndentFor(before);
          if (before.name == 'BlockLiteral')
            return cx.baseIndentFor(before) + cx.unit;
          if (before.name == 'BlockSequence' || before.name == 'BlockMapping')
            return cx.column(before.from, 1);
          if (before.name == 'QuotedLiteral') return null;
          if (before.name == 'Literal') {
            const col = cx.column(before.from, 1);
            if (col == cx.lineIndent(before.from, 1)) return col; // Start on own line
            if (before.to > cx.pos) return null;
          }
        }
        return null;
      },
      FlowMapping: delimitedIndent({ closing: '}' }),
      FlowSequence: delimitedIndent({ closing: ']' }),
    }),
  ],
});

const yamlLanguage = LRLanguage.define({
  name: 'yaml',
  parser: yamlParser,
  languageData: {
    commentTokens: { line: '#' },
    indentOnInput: /^\s*[\]}]$/,
  },
});

export const DATA_CONVERTERS: Record<
  'json' | 'yaml',
  {
    parse: <T>(value: string) => T;
    stringify: <T>(value: T) => string;
    language: LanguageSupport;
    extensions?: Extension[];
  }
> = {
  json: {
    parse: (value) => JSON.parse(value),
    stringify: (value) => JSON.stringify(value, null, 2),
    language: json(),
  },
  yaml: {
    parse: (value) => YAML.parse(value),
    stringify: (value) =>
      (YAML.stringify(value, { lineWidth: 0 }) as string | undefined)?.slice(
        0,
        -1
      ) ?? '',
    language: new LanguageSupport(yamlLanguage),
    extensions: [
      foldService.of((state, _, to) => {
        const tree = syntaxTreeAvailable(state)
          ? syntaxTree(state)
          : yamlParser.parse(state.doc.toString());
        if (tree.length < to) return null;

        const end = tree.resolveInner(to, 1);
        if (end.node.type.name === 'Pair') {
          return { from: to, to: end.node.to };
        }

        return null;
      }),
    ],
  },
};

// foldAll command, which avoids collapsing the root node
// which is not useful for JSON
export const foldAllExceptRoot = (view: EditorView) => {
  const { state } = view;
  const effects: Array<StateEffect<unknown>> = [];
  for (let pos = 0; pos < state.doc.length; ) {
    const line = view.lineBlockAt(pos);
    let range = foldable(state, line.from, line.to);

    // avoid root node
    if (range && range.from + range.to === state.doc.length) {
      range = null;
    }

    if (range) effects.push(foldEffect.of(range));
    pos = (range ? view.lineBlockAt(range.to) : line).to + 1;
  }
  if (effects.length) {
    view.dispatch({ effects: effects });
  }
  return !!effects.length;
};

export const useEditorTheme = (
  diffValues?: boolean,
  variant: 'plain' | 'full' = 'full'
) => {
  const { isDarkMode } = useColorScheme();
  const theme = useTheme();
  return EditorView.theme({
    '&.cm-editor': {
      backgroundColor: 'transparent',
    },
    '&.cm-focused': {
      outline: 'none',
    },
    green: {
      background: 'green',
    },
    '& .cm-content': {
      padding: '12px',
    },
    '& .cm-line': {
      fontFamily: "'Fira Code', monospace",
      padding: 0,
      overflowAnchor: 'none',
      fontVariantLigatures: 'none',
      // if diff'ing, make all text the same color
      ...(diffValues && {
        color: isDarkMode ? '#ffffff' : '#000000',
      }),
    },
    // if diff'ing, make all text the same color
    '& span': {
      ...(diffValues && {
        color: isDarkMode ? '#ffffff' : '#000000',
      }),
    },
    '& .cm-gutters.cm-gutters': {
      backgroundColor: 'transparent',
      color: theme.palette.text.tertiary,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    '& .cm-lineNumbers .cm-gutterElement.cm-activeLineGutter': {
      marginLeft: '1px',
    },
    '& .cm-lineNumbers': {
      minWidth: '42px',
    },
    '& .cm-foldPlaceholder': {
      padding: '0px 4px',
      color: 'var(--gray-100)',
      backgroundColor: 'var(--gray-50)',
      borderColor: 'var(--gray-100)',
    },
    '& .cm-gutterElement span[title="Fold line"]': {
      transform: 'translateY(-4px)',
      display: 'inline-block',
    },
    '.highlight-added': {
      backgroundColor: isDarkMode
        ? theme.palette.success[600]
        : theme.palette.success[300],
    },
    '.highlight-removed': {
      backgroundColor: isDarkMode
        ? theme.palette.danger[600]
        : theme.palette.danger[300],
    },
    ...(variant === 'plain' && {
      '& .cm-activeLineGutter': {
        background: 'transparent !important',
      },

      '& .cm-gutterElement span': {
        marginLeft: '3px',
      },

      '& .cm-gutters.cm-gutters': {
        backgroundColor: 'transparent',
        color: theme.palette.text.tertiary,
        borderRight: `none`,
      },
      '& .cm-content': {
        padding: '12px',
        paddingLeft: '1px',
      },
    }),
  });
};
